import type { DodgeballMatchPenalty } from '@/types/sports/dodgeball';
import { localStorage } from '@/utils/local-storage';

export const matchTimeStore = localStorage('game-time', 0);
export const timeoutTimeStore = localStorage('timeout-time', 0);
export const resetTimeStore = localStorage('reset-time', 0);
export const setTimeStore = localStorage('set-time', 0);
export const updateTimeStore = localStorage('update-time', 0);
export const lastUpdateStore = localStorage<number | null>('last-update', null);

const homePenaltiesStore = localStorage<DodgeballMatchPenalty[]>(
  'home-penalties',
  [],
);
const awayPenaltiesStore = localStorage<DodgeballMatchPenalty[]>(
  'away-penalties',
  [],
);

export const penaltiesStore = {
  home: homePenaltiesStore,
  away: awayPenaltiesStore,
};

export const setTimings = (
  id: string,
  {
    timings,
    lastUpdate,
  }: {
    timings: {
      match_time: number;
      set_time: number;
      reset_time: number;
      timeout_time: number;
    };
    lastUpdate: number;
  },
) => {
  matchTimeStore.set(id, timings.match_time);
  setTimeStore.set(id, timings.set_time);
  timeoutTimeStore.set(id, timings.timeout_time);
  resetTimeStore.set(id, timings.reset_time);
  updateTimeStore.set(id, lastUpdate);
};

export const resetTimings = (id: string) => {
  matchTimeStore.set(id);
  setTimeStore.set(id);
  timeoutTimeStore.set(id);
  resetTimeStore.set(id);
  updateTimeStore.set(id);
  homePenaltiesStore.set(id);
  awayPenaltiesStore.set(id);
};

export const renderTimings = (
  id: string,
  update: (timings: {
    matchTime: number;
    resetTime: number;
    setTime: number;
    timeoutTime: number;
  }) => void,
) => {
  const matchTime = matchTimeStore.get(id);
  const resetTime = resetTimeStore.get(id);
  const setTime = setTimeStore.get(id);
  const timeoutTime = timeoutTimeStore.get(id);

  update({ matchTime, resetTime, setTime, timeoutTime });
};

export const renderPenaltyData = (
  id: string,
  update: (data: {
    homeTeam: DodgeballMatchPenalty[];
    awayTeam: DodgeballMatchPenalty[];
  }) => void,
) => {
  const homeTeam = homePenaltiesStore.get(id);
  const awayTeam = awayPenaltiesStore.get(id);

  update({ homeTeam, awayTeam });
};

export const updateTimings = (
  id: string,
  { match_state, set_state }: { match_state: string; set_state: string },
) => {
  if (!id) {
    return;
  }

  const lastUpdate = updateTimeStore.get(id);

  if (!lastUpdate) {
    return;
  }

  const newTiming = performance.now();
  const delta = (newTiming - (lastUpdate - performance.timeOrigin)) / 1000.0;
  updateTimeStore.set(id, performance.timeOrigin + newTiming);

  if (match_state === 'running') {
    matchTimeStore.set(id, Math.max(matchTimeStore.get(id) - delta, 0));

    if (set_state === 'running') {
      setTimeStore.set(id, Math.max(setTimeStore.get(id) - delta, 0));
    } else if (set_state === 'finished') {
      resetTimeStore.set(id, Math.max(resetTimeStore.get(id) - delta, 0));
    }

    ['home', 'away'].forEach((teamName) => {
      // TypeScript doesn't know it's only these two values
      const team = teamName as 'home' | 'away';

      const penalties = penaltiesStore[team].get(id);

      if (penalties) {
        penalties.forEach((penalty) => {
          if (!penalty.time) {
            return;
          }

          penalty.time = Math.max(penalty.time - delta, 0);
        });

        penaltiesStore[team].set(id, penalties);
      }
    });
  } else if (match_state === 'timeout' || match_state === 'break') {
    timeoutTimeStore.set(id, Math.max(timeoutTimeStore.get(id) - delta, 0));
  }
};
